import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth-store.js';
import { useConfigurationStore } from '@/stores/configuration-store.js';
import { startProgress, doneProgress, forceDoneProgress } from '@/composables/useRouteProgress';
import { resolveRouteTitle, setPageTitle } from '@/composables/usePageTitle.js';
import { useToast } from '@/composables/useToast.js';
import { getConnectivityStatus, isDegraded } from '@/services/connectivityState.js';

/** Why this screen is unavailable, in terms the operator can act on. */
const REQUIRES_ONLINE_MESSAGES = {
    offline: 'You are offline — this screen needs a connection.',
    'backend-unavailable': 'The server is unavailable — this screen needs a connection. Retrying automatically.',
    'expired-session': 'Your session has expired — please sign in again to use this screen.',
};

// layouts main is for authenticated users
// layouts auth is for performing auth actions
// layouts public is for all type of users
// layouts blank is for error pages, etc

const router = createRouter({
    history: createWebHistory('/'),
    routes: [
        // =======================================
        // Public Routes
        // =======================================

{
    path: '/refund-return',
    name: 'refund_return',
    component: () => import('@/pages/Portal/Refunds/RefundReturn.vue'),
    meta: {
        title: 'Refund & Return',
        layout: 'main',
        accessTypes: ['authenticated'],
        accessPermissions: ['manage_refunds'],
        accessCondition: 'AND',
    },
},
{
    path: '/refund/:id',
    name: 'refund_details',
    component: () => import('@/pages/Portal/Refunds/RefundDetails.vue'),
    meta: {
        title: (route) => `Refund #${route.params.id}`,
        layout: 'main',
        accessTypes: ['authenticated'],
        accessPermissions: ['manage_refunds'],
        accessCondition: 'AND',
    },
},
        {
            path: '/sale/:id',
            name: 'sale.show',
            component: () => import('@/components/Portal/Sales/SalesDetail.vue'),
            meta: {
                title: (route) => `Sale #${route.params.id}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_sales'],
                accessCondition: 'AND',
            }
        },

        {
            path: '/',
            redirect: { name: 'login' },
        },

        // =======================================

        {
            path: '/dashboard',
            name: 'dashboard',
            component: () => import('@/pages/Portal/Dashboard.vue'),
            meta: {
                title: 'Dashboard',
                layout: 'main',
                accessTypes: ['authenticated'],
            },
        },

        {
            path: '/settings',
            name: 'settings',
            component: () => import('@/pages/Portal/Settings.vue'),
            meta: {
                title: 'Settings',
                layout: 'main',
                accessTypes: ['authenticated'],
            },
        },
        {
            path: '/help',
            name: 'help',
            component: () => import('@/pages/Portal/Help/Index.vue'),
            meta: {
                title: 'Help & Tours',
                layout: 'main',
                accessTypes: ['authenticated'],
            },
        },
        // =======================================
        // Authenticated User Routes
        // =======================================
        {
            path: '/attributes',
            name: 'attributes',
            component: () => import('@/pages/Portal/Attributes/Index.vue'),
            meta: {
                title: 'Attributes',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_categories', 'manage_units', 'manage_sizes', 'manage_materials', 'manage_colors', 'manage_types', 'manage_accounts'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/order/print/:id',
            name: 'OrderPrint',
            component: () => import('@/components/OrderPrint.vue'),
            meta: {
                // This route prints a purchase order, not a sales order.
                title: (route) => `Purchase Order #${route.params.id} — Print`,
                // A print route carries no app chrome, but it still has to name a
                // layout: App.vue mounts the shell above RouterView and renders
                // nothing at all when `meta.layout` resolves to no layout.
                layout: 'blank',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_purchases'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/receipt/:id',
            name: 'ReceiptView',
            component: () => import('@/components/Receipt.vue'),
            meta: {
                title: (route) => `Receipt #${route.params.id}`,
                layout: 'blank',
                accessTypes: ['authenticated'],
                // Serves purchase payment receipts and, with `?type=receiving`,
                // receiving vouchers — reached from the purchase and the supplier
                // side, so it admits the permission that guards either entry.
                accessPermissions: ['manage_purchases', 'manage_payments', 'manage_suppliers'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/suppliers',
            name: 'suppliers',
            component: () => import('@/pages/Portal/Suppliers/Suppliers.vue'),
            meta: {
                title: 'Suppliers',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_suppliers'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/supplierDetails/:id',
            name: 'supplierDetails',
            component: () => import('@/pages/Portal/Suppliers/SupplierDetails.vue'),
            props: true,
            meta: {
                title: (route) => `Supplier #${route.params.id}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_suppliers'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/purchase/:id',
            name: 'PurchaseDetails',
            component: () => import('@/components/Portal/Purchases/PurchaseDetails.vue'),
            props: (route) => ({
                id: route.params.id,
            }),
            meta: {
                title: (route) => `Purchase #${route.params.id}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_purchases'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/purchases/AddEditPurchaseComponent/:id?',
            name: 'purchases.AddEditPurchaseComponent',
            component: () => import('@/components/Portal/Purchases/AddEditPurchaseComponent.vue'),
            meta: {
                title: (route) => (route.params.id ? `Edit Purchase #${route.params.id}` : 'New Purchase'),
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_purchases'],
                accessCondition: 'AND',
                requiresOnline: true,
            },
        },

        {
            path: '/ask',
            name: 'ask',
            component: () => import('@/components/Portal/Ask/AskPanel.vue'),
            meta: {
                title: 'Ask Inventree',
                layout: 'main',
                accessTypes: ['authenticated'],
                // No accessPermissions on purpose. What somebody may ask about
                // is decided table by table by AskCatalogue::userMayName(); a
                // permission here would either duplicate that or contradict it,
                // and a user with no reachable table already gets an empty
                // vocabulary and a refusal before anything is spent.
                addonFeature: 'ask_inventree',
                // Every question calls a third-party model, so there is nothing
                // useful to do here offline. Past answers stay readable, but a
                // new one is refused rather than queued — there is no mutation
                // outbox and this is not the place to invent one.
                requiresOnline: true,
            },
        },

        {
            path: '/purchases/extractions',
            name: 'purchases.extractions',
            component: () => import('@/components/Portal/Purchases/PurchaseExtractions.vue'),
            meta: {
                title: 'AI Purchase Order Extractions',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_purchases'],
                accessCondition: 'AND',
                addonFeature: 'ai_purchase_extraction',
                // Not cached offline: a draft can only be turned into an order
                // online anyway, and the rows carry supplier and pricing read
                // off someone's invoice.
                requiresOnline: true,
            },
        },

        {
            path: '/purchases/PurchasePaymentModel',
            name: 'purchases.PurchasePaymentModel',
            component: () => import('@/components/Portal/Purchases/PurchasePaymentModel.vue'),
            meta: {
                title: 'Purchase Payment',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_purchases', 'manage_payments'],
                accessCondition: 'AND',
                requiresOnline: true,
            },
        },
        {
            path: '/purchases/ReceivingModel',
            name: 'purchases.ReceivingModel',
            component: () => import('@/components/Portal/Purchases/ReceivingModel.vue'),
            meta: {
                title: 'Receive Stock',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_purchases'],
                accessCondition: 'AND',
                requiresOnline: true,
            },
        },
        {
            path: '/purchases/Receiving',
            name: 'Receiving',
            component: () => import('@/components/Portal/Purchases/Receiving.vue'),
            meta: {
                title: 'Receiving',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_purchases'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/purchase/Payment',
            name: 'Payment',
            component: () => import('@/components/Portal/Purchases/Payment.vue'),
            meta: {
                title: 'Purchase Payments',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_purchases', 'manage_payments'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/customers',
            name: 'customers',
            component: () => import('@/pages/Portal/Customers/Customers.vue'),
            meta: {
                title: 'Customers',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_customers', 'manage_wholesalers'],
                accessCondition: 'AND',
            },
        },



        {
            path: '/automotives',
            name: 'automotives',
            component: () => import('@/pages/Portal/Automotives/Automotives.vue'),
            meta: {
                title: 'Automotive',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_automotives'],
                accessCondition: 'AND',
                addonFeature: 'automotive_management',
            },
        },

        {
            path: '/automotives/:id',
            name: 'automotiveDetails',
            component: () => import('@/pages/Portal/Automotives/AutomotiveDetails.vue'),
            meta: {
                title: (route) => `Vehicle #${route.params.id}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_automotives'],
                accessCondition: 'AND',
                addonFeature: 'automotive_management',
            },
        },

        {
            path: '/reminders',
            name: 'reminders',
            component: () => import('@/pages/Portal/Reminders/Index.vue'),
            meta: {
                title: 'Reminders',
                layout: 'main',
                accessTypes: ['authenticated'],
                // OR-ed: the specification says anyone with Sales or Customers
                // access manages reminders, which is why there is no
                // reminder-specific permission to require here.
                accessPermissions: ['manage_sales', 'manage_customers', 'manage_wholesalers'],
                accessCondition: 'OR',
                addonFeature: 'customer_reminders',
                // Every endpoint behind this screen is a mutation, and offline
                // is read-only for server data.
                requiresOnline: true,
            },
        },

        {
            path: '/branches',
            name: 'branches',
            component: () => import('@/pages/Portal/Branches/Index.vue'),
            meta: {
                title: 'Branches',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_branches'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/products',
            name: 'products',
            component: () => import('@/pages/Portal/Products/Index.vue'),
            meta: {
                title: 'Products',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_products'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/transfers/:id',
            name: 'transfer-details',
            component: () => import('@/components/Portal/Transfers/TransferDetails.vue'),
            meta: {
                title: (route) => `Transfer #${route.params.id}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_transfer'],
                accessCondition: 'AND',
            }
        },

        {
            path: '/transfers/:id/print',
            name: 'TransferPrint',
            component: () => import('@/pages/Portal/Transfers/TransferPrint.vue'),
            meta: {
                title: (route) => `Transfer #${route.params.id} — Print`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_transfer'],
                accessCondition: 'AND',
            }
        },

        {
            path: '/sales',
            name: 'sales',
            component: () => import('@/pages/Portal/Sales/Index.vue'),
            meta: {
                title: 'Sales',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_sales'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/sales-report',
            redirect: { name: 'reports.sales' },
        },
        {
            path: '/pos',
            name: 'pos',
            component: () => import('@/pages/Portal/POS/Index.vue'),
            meta: {
                title: 'Point of Sale',
                // POS runs its own shell (a till-specific topbar). It used to be
                // selected by which Layout the page imported; now that App.vue
                // mounts the shell, the route has to name it.
                layout: 'sales',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_sales'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/purchases',
            name: 'purchases',
            component: () => import('@/pages/Portal/Purchases/Index.vue'),
            meta: {
                title: 'Purchases',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_purchases'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/purchases/report',
            redirect: { name: 'reports.purchases' },
        },
        // The reports hub. Its permission list is the union of the reports it
        // links to, so anyone who can open at least one report can reach the
        // page — the individual routes below stay the authority for each one.
        {
            path: '/reports',
            name: 'reports',
            component: () => import('@/pages/Portal/Reports/Index.vue'),
            meta: {
                title: 'Reports',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: [
                    'view sale report',
                    'view purchase report',
                    'view expense report',
                    'view_customer_report',
                    'view_supplier_report',
                    'view_product_report',
                    'view_variant_report',
                    'view_tax_report',
                    'view_profit_loss_report',
                    'view_refund_report',
                    'view_transfer_report',
                    'view_branch_report',
                    'manage_ledger',
                ],
                accessCondition: 'AND',
            },
        },
        {
            path: '/reports/sales',
            name: 'reports.sales',
            component: () => import('@/components/Portal/Sales/SalesReport.vue'),
            meta: {
                title: 'Sales Report',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view sale report'],
                accessCondition: 'AND',
            },
        },
        // General ledger reporting. manage_ledger mirrors the check every
        // LedgerController action makes; the controller stays the authority.
        // The addon gate is on top of it: with general_ledger off nothing is
        // posted, so every one of these reads as an empty or misleading report.
        {
            path: '/reports/trial-balance',
            name: 'reports.trialBalance',
            component: () => import('@/components/Portal/Reports/TrialBalance.vue'),
            meta: {
                title: 'Trial Balance',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_ledger'],
                accessCondition: 'AND',
                addonFeature: 'general_ledger',
            },
        },
        {
            path: '/reports/ledger/:code',
            name: 'reports.ledger.account',
            component: () => import('@/components/Portal/Reports/GeneralLedger.vue'),
            meta: {
                title: (route) => `Ledger ${route.params.code}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_ledger'],
                accessCondition: 'AND',
                addonFeature: 'general_ledger',
            },
        },
        {
            path: '/reports/financial-statements',
            name: 'reports.financialStatements',
            component: () => import('@/components/Portal/Reports/FinancialStatements.vue'),
            meta: {
                title: 'Financial Statements',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_ledger'],
                accessCondition: 'AND',
                addonFeature: 'general_ledger',
            },
        },
        {
            path: '/admin/chart-of-accounts',
            name: 'admin.chartOfAccounts',
            component: () => import('@/pages/Portal/Admin/ChartOfAccounts.vue'),
            meta: {
                title: 'Chart of Accounts',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_ledger'],
                accessCondition: 'AND',
                addonFeature: 'general_ledger',
            },
        },
        {
            path: '/reports/purchases',
            name: 'reports.purchases',
            component: () => import('@/components/Portal/Purchases/PurchaseReport.vue'),
            meta: {
                title: 'Purchase Report',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view purchase report'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/reports/products',
            name: 'reports.products',
            component: () => import('@/components/Portal/Reports/ProductReport.vue'),
            meta: {
                title: 'Product Report',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view_product_report'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/reports/variants',
            name: 'reports.variants',
            component: () => import('@/components/Portal/Reports/ProductVariantReport.vue'),
            meta: {
                title: 'Variant Report',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view_variant_report'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/reports/suppliers',
            name: 'reports.suppliers',
            component: () => import('@/components/Portal/Reports/SupplierReport.vue'),
            meta: {
                title: 'Supplier Report',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view_supplier_report'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/reports/customers',
            name: 'reports.customers',
            component: () => import('@/components/Portal/Reports/CustomerReport.vue'),
            meta: {
                title: 'Customer Report',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view_customer_report'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/reports/tax',
            name: 'reports.tax',
            component: () => import('@/components/Portal/Reports/TaxReport.vue'),
            meta: {
                title: 'Tax Report',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view_tax_report'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/reports/profit-loss',
            name: 'reports.profit-loss',
            component: () => import('@/components/Portal/Reports/ProfitLossReport.vue'),
            meta: {
                title: 'Profit & Loss Report',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view_profit_loss_report'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/reports/refunds',
            name: 'reports.refunds',
            component: () => import('@/components/Portal/Reports/RefundReport.vue'),
            meta: {
                title: 'Refund Report',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view_refund_report'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/reports/transfers',
            name: 'reports.transfers',
            component: () => import('@/components/Portal/Reports/TransferReport.vue'),
            meta: {
                title: 'Transfer Report',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view_transfer_report'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/reports/branch',
            name: 'reports.branch',
            component: () => import('@/components/Portal/Reports/BranchReport.vue'),
            meta: {
                title: 'Branch Report',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view_branch_report'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/reports/expense',
            name: 'reports.expense',
            component: () => import('@/components/Portal/Reports/ExpenseReport.vue'),
            meta: {
                title: 'Expense Report',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view expense report'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/reports/damaged-items',
            name: 'reports.damaged-items',
            component: () => import('@/components/Portal/Reports/DamagedReport.vue'),
            meta: {
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_damaged_items'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/transfers',
            name: 'transfers',
            component: () => import('@/pages/Portal/Transfers/Index.vue'),
            meta: {
                title: 'Transfers',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_transfer'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/expenses',
            name: 'expenses',
            component: () => import('@/pages/Portal/Expenses/Index.vue'),
            meta: {
                title: 'Expenses',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_expenses'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/expenses/:id',
            name: 'ExpenseDetails',
            component: () => import('@/pages/Portal/Expenses/ExpenseDetails.vue'),
            meta: {
                title: (route) => `Expense #${route.params.id}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_expenses'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/stock-discrepancies',
            name: 'stock_discrepancies',
            component: () => import('@/pages/Portal/StockDiscrepancies/Index.vue'),
            meta: {
                title: 'Stock Discrepancies',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_stock_discrepancies'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/trash',
            name: 'trash',
            component: () => import('@/pages/Portal/Trash/Index.vue'),
            meta: {
                title: 'Trash',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_trash'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/exports',
            name: 'exports',
            component: () => import('@/pages/Portal/Exports/Index.vue'),
            meta: {
                title: 'Exports',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['export_data'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/imports',
            name: 'imports',
            component: () => import('@/pages/Portal/Imports/Index.vue'),
            meta: {
                title: 'Imports',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['import_data'],
                accessCondition: 'AND',
            },
        },



        {
            path: '/customers/:id',
            name: 'customerDetails',
            component: () => import('@/pages/Portal/Customers/CustomerDetails.vue'),
            meta: {
                title: (route) => `Customer #${route.params.id}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_customers'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/wholesalers/:id',
            name: 'wholesalerDetails',
            component: () => import('@/pages/Portal/Customers/WholesalerDetails.vue'),
            meta: {
                title: (route) => `Wholesaler #${route.params.id}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_wholesalers'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/supplier-returns',
            name: 'supplier_returns',
            component: () => import('@/pages/Portal/SupplierReturns/Index.vue'),
            meta: {
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_supplier_returns'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/damaged-items',
            name: 'damaged_items',
            component: () => import('@/pages/Portal/DamagedItems/Index.vue'),
            meta: {
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_damaged_items'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/damaged-items/:id',
            name: 'damaged_item_details',
            component: () => import('@/pages/Portal/DamagedItems/Details.vue'),
            meta: {
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_damaged_items'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/gate-passes',
            name: 'gate_passes',
            component: () => import('@/pages/Portal/GatePasses/GatePassList.vue'),
            meta: {
                title: 'Gate Passes',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_gate_passes'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/gate-passes/:gatepass_no',
            name: 'GatePassDetails',
            component: () => import('@/pages/Portal/GatePasses/GatePassDetails.vue'),
            meta: {
                title: (route) => `Gate Pass ${route.params.gatepass_no}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_gate_passes'],
                accessCondition: 'AND',
            },
        },

        {
            // Drivers are shared master data — either module that uses them may
            // reach the directory, so this is deliberately not addon-gated.
            path: '/delivery-people',
            name: 'delivery_persons',
            component: () => import('@/pages/Portal/DeliveryPersons/DeliveryPersons.vue'),
            meta: {
                title: 'Delivery People',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_load_forms', 'manage_gate_passes'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/delivery-people/:id',
            name: 'delivery_person_details',
            component: () => import('@/pages/Portal/DeliveryPersons/DeliveryPersonDetails.vue'),
            meta: {
                title: 'Delivery Person',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_load_forms', 'manage_gate_passes'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/load-forms',
            name: 'load_forms',
            component: () => import('@/pages/Portal/LoadForms/LoadFormList.vue'),
            meta: {
                title: 'Load Forms',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_load_forms'],
                accessCondition: 'AND',
                addonFeature: 'load_form_delivery',
            },
        },

        {
            path: '/load-forms/:id',
            name: 'load_form_details',
            component: () => import('@/pages/Portal/LoadForms/LoadFormDetails.vue'),
            meta: {
                title: (route) => `Load Form #${route.params.id}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_load_forms'],
                accessCondition: 'AND',
                addonFeature: 'load_form_delivery',
            },
        },

        {
            path: '/load-forms/:id/print',
            name: 'load_form_print',
            component: () => import('@/pages/Portal/LoadForms/LoadFormPrint.vue'),
            meta: {
                title: (route) => `Load Form #${route.params.id} — Print`,
                layout: 'blank',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_load_forms'],
                accessCondition: 'AND',
                addonFeature: 'load_form_delivery',
                requiresOnline: true,
            },
        },

        {
            path: '/w/:token',
            name: 'wholesaler-public',
            component: () => import('@/pages/Portal/Customers/WholesalerPublicPage.vue'),
            meta: {
                title: 'Wholesaler Portal',
                layout: 'blank',
                accessTypes: ['guest', 'authenticated', 'admin'],
            },
        },

        {
            path: '/wholesaler-order-requests',
            name: 'RequestOrderList',
            component: () => import('@/pages/Portal/Orders/RequestOrderList.vue'),
            meta: {
                title: 'Order Requests',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view_order_requests'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/wholesaler-order-requests/:id',
            name: 'OrderDetails',
            component: () => import('@/pages/Portal/Orders/OrderDetails.vue'),
            meta: {
                title: (route) => `Order Request #${route.params.id}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['view_order_requests'],
                accessCondition: 'AND',
            },
        },

        // =======================================
        // Authenticated Admin Routes
        // =======================================
        {
            path: '/accounts/:id',
            name: 'account-details',
            component: () => import('@/pages/Portal/Accounts/AccountDetails.vue'),
            meta: {
                title: (route) => `Account #${route.params.id}`,
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_accounts'],
                accessCondition: 'AND',
            },
        },
        {
            path: '/admin/users',
            name: 'manage_users',
            component: () => import('@/pages/Portal/Admin/UserManagement.vue'),
            meta: {
                title: 'User Management',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_users', 'manage_roles'],
                accessCondition: 'AND',
            },
        },

        {
            path: '/admin/configurations',
            name: 'manage_configurations',
            component: () => import('@/pages/Portal/Admin/Configurations.vue'),
            meta: {
                title: 'Configurations',
                layout: 'main',
                accessTypes: ['authenticated'],
                accessPermissions: ['manage_configurations'],
                accessCondition: 'AND',
            },
        },
        // =======================================
        // Route for Performing Authentication
        // =======================================
        {
            path: '/register',
            name: 'register',
            redirect: { name: 'login' },
        },
        {
            path: '/login',
            name: 'login',
            component: () => import('@/pages/Auth/Login.vue'),
            meta: {
                title: 'Sign In',
                layout: 'auth',
                accessTypes: ['guest'],
            },
        },
        {
            path: '/force-password',
            name: 'force_password',
            component: () => import('@/pages/Auth/ForcePasswordChange.vue'),
            meta: {
                title: 'Change Password',
                layout: 'auth',
                accessTypes: ['authenticated'],
            },
        },
        {
            path: '/forgot',
            name: 'forgot',
            component: () => import('@/pages/Auth/Forgot.vue'),
            meta: {
                title: 'Forgot Password',
                layout: 'auth',
                accessTypes: ['guest'],
            },
        },
        {
            path: '/reset',
            name: 'reset',
            component: () => import('@/pages/Auth/Reset.vue'),
            meta: {
                title: 'Reset Password',
                layout: 'auth',
                accessTypes: ['guest'],
            },
        },
        {
            name: 'email_verification',
            path: '/email/verify/:id/:hash',
            component: () => import('@/pages/Auth/EmailVerification.vue'),
            props: (route) => ({
                id: route.params.id,
                expires: route.query.expires,
                hash: route.params.hash,
                signature: route.query.signature,
            }),
            meta: {
                title: 'Email Verification',
                layout: 'auth',
                accessTypes: ['guest'],
            },
        },
        {
            name: "reset_password",
            path: "/password/reset/:token",
            component: () => import('@/pages/Auth/Reset.vue'),
            props: route => ({
                token: route.params.token,
                email: route.query.email
            }),
            meta: {
                title: 'Reset Password',
                layout: 'auth',
                accessTypes: ['guest'],
            }
        },
        // Add this to your router configuration file (usually router/index.js or routes.js)

        {
            path: '/branches/:id',
            name: 'branch-details',
            component: () => import('@/components/Portal/Branches/BranchDetails.vue'),
            meta: {
                title: (route) => `Branch #${route.params.id}`,
                layout: 'main',
                accessTypes: ['authenticated'],
            }
        }


        // =======================================
        // Route for Error Handling
        // =======================================
        , {
            path: '/:pathMatch(.*)',
            name: 'error',
            component: () => import('@/pages/Error.vue'),
            meta: {
                title: 'Page Not Found',
                layout: 'blank',
            },
        }

    ],
    scrollBehavior() {
        return { top: 0 };
    },
});

let firstLoad = true;
const routerProgressGenerations = new WeakMap();
let openProgressRoute = null;

/**
 * Hand back the progress token a navigation took, at most once.
 *
 * vue-router does not run afterEach for a navigation that a guard redirected
 * away from: next({ name: … }) abandons that navigation and starts a fresh one,
 * and only the final destination reaches afterEach. Pairing the token with
 * afterEach alone therefore leaked one count on every redirect — and since the
 * counter never returned to zero again, the top bar trickled on forever until
 * the 70s watchdog force-cleared it. Releasing the outstanding token as the next
 * navigation starts closes that gap; an abandoned route whose afterEach does
 * still run (cancelled navigations get one) finds nothing left to release.
 */
function releaseRouteProgress(route) {
    if (!route) return;
    const generation = routerProgressGenerations.get(route);
    routerProgressGenerations.delete(route);
    if (openProgressRoute === route) {
        openProgressRoute = null;
    }
    if (generation !== undefined) {
        doneProgress(generation);
    }
}

router.beforeResolve(async (to, from, next) => {
    // Take the new token before releasing the old one so the pending count never
    // touches zero mid-redirect, which would restart NProgress' trickle loop.
    const abandoned = openProgressRoute;
    routerProgressGenerations.set(to, startProgress());
    openProgressRoute = to;
    if (abandoned !== to) {
        releaseRouteProgress(abandoned);
    }

    const authStore = useAuthStore();

    const fromLayout = from.meta?.layout;
    const toLayout = to.meta?.layout;
    const reloadGroup = ['auth', 'main'];

    const layoutChanged = fromLayout !== toLayout;
    const layoutNeedsReload = reloadGroup.includes(toLayout);

    if ((layoutChanged && layoutNeedsReload) || firstLoad) {
        firstLoad = false;

        if (toLayout !== 'public') {
            await authStore.FETCH_USER_DATA(true);
        }
    }

    // Gate on a *settled* degraded verdict, never on the absence of one. Boot's
    // `checking` is not evidence of anything, and bouncing a route because a
    // probe happened to be in flight is how a plain refresh on one of these
    // pages used to land the operator somewhere else entirely.
    if (to.meta?.requiresOnline && isDegraded()) {
        useToast().displayToast({
            message: REQUIRES_ONLINE_MESSAGES[getConnectivityStatus()] ?? REQUIRES_ONLINE_MESSAGES.offline,
            variant: 'warning',
            duration: 6000,
        });
        if (from?.name) {
            next(false);
        } else {
            // The one route with no permission requirement — `purchases` is
            // gated, so an operator without it was bounced into a second denial.
            next({ name: 'dashboard' });
        }
        return;
    }

    // Deep-link gate for addon-only routes. This guard runs before App.vue's
    // setup (app.js awaits router.isReady() before mount), so the cached
    // snapshot is hydrated here rather than assumed. When the addon state is
    // genuinely unknown we allow through: the server gates every endpoint
    // anyway, and bouncing a cold hard-refresh is the worse failure.
    if (to.meta?.addonFeature) {
        const configStore = useConfigurationStore();
        if (!configStore.addonsLoaded && configStore.enabledAddonKeys.length === 0) {
            configStore.loadFromCache();
        }
        const addonStateKnown = configStore.addonsLoaded || configStore.enabledAddonKeys.length > 0;
        if (addonStateKnown && !configStore.isAddonEnabled(to.meta.addonFeature)) {
            next({ name: 'dashboard' });
            return;
        }
    }

    const mustChangePassword = authStore.isLoggedIn
        && Boolean(authStore.user?.must_change_password)
        && !authStore.user?.is_owner;

    if (mustChangePassword && to.name !== 'force_password') {
        next({ name: 'force_password' });
        return;
    }

    if (!mustChangePassword && to.name === 'force_password') {
        next({ name: 'dashboard' });
        return;
    }

    if (authStore.canAccessRoute(to)) {
        next();
    }
    else {
        if (!authStore.isLoggedIn) {
            next({ name: 'login' });
        }
        // A logged-in shell that holds no permissions and never verified against
        // the server is a stale localStorage identity whose session has gone, not
        // a user who lacks access. Sending it to the dashboard strands the operator
        // on the one route with no permission requirement, with every menu hidden
        // and no way back to the login screen. Offline is excluded: there the
        // cached identity is deliberately kept so read-only work continues.
        else if (authStore.getOnlineStatus && !authStore.userDataFetched && authStore.getPermissions.length === 0) {
            authStore.CLEAR_USER_DATA();
            next({ name: 'login' });
        }
        else {
            next({ name: mustChangePassword ? 'force_password' : 'dashboard' });
        }
    }
});

router.afterEach((to) => {
    // Every page owns its tab title through meta.title; routes that only
    // redirect declare none and fall back to the bare company name.
    setPageTitle(resolveRouteTitle(to));

    releaseRouteProgress(to);
});

/**
 * A route component that fails to import means this tab is running a build the
 * server has already replaced: every chunk is content-hashed, so `npm run build`
 * rewrites the filenames the loaded shell is still asking for and the lazy import
 * 404s. Reloading fetches the current shell and its current hashes.
 *
 * Two guards keep the reload from becoming its own failure mode. A sessionStorage
 * stamp means a chunk that is genuinely absent — a half-finished deploy — surfaces
 * as a visible error instead of a reload loop. And `navigator.onLine === false` is
 * proof of no network (unlike a `true`, which proves nothing), so an offline tab
 * whose chunk simply is not in the service-worker cache keeps its state rather
 * than reloading into the same miss.
 */
const STALE_CHUNK_RELOAD_KEY = 'inventree_stale_chunk_reload';
const STALE_CHUNK_RELOAD_WINDOW_MS = 20000;

function isStaleChunkError(error) {
    // One message per engine, plus Vite's own preload helper for the CSS side.
    return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css/i
        .test(String(error?.message ?? error));
}

function reloadForStaleChunk() {
    if (navigator.onLine === false) {
        return;
    }

    let lastReloadAt = 0;
    try {
        lastReloadAt = Number(sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY)) || 0;
    } catch {
        // Storage is unavailable (private mode, blocked cookies); read as never reloaded.
    }

    if (Date.now() - lastReloadAt < STALE_CHUNK_RELOAD_WINDOW_MS) {
        return;
    }

    try {
        sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
        // Without the stamp the window guard cannot arm, so reload at most this once.
    }

    window.location.reload();
}

router.onError((error) => {
    openProgressRoute = null;
    forceDoneProgress();

    if (isStaleChunkError(error)) {
        reloadForStaleChunk();
    }
});

export default router;
